import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import hazardIcon from '../../images/hazardIcon.svg';

// Custom hook to detect mobile viewport
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 767);
  React.useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 767);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

import Dropdown from '../dropdown/dropdown';
import InfoTooltip from '../infoTooltip/infoTooltip';
import './styles.css';
import { buildDynamicFilters } from './allProviderFilters';
import closeIcon from '../../images/close.svg';
import infoCircle from '../../images/infoCircle.svg';
import arrowDown from '../../images/arrowDown.svg';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';

const ProviderFilters = ({ filters: externalFilters, onFilterChange: externalFilterChange, onBatchFilterChange, facets }) => {
  const isMobile = useIsMobile();
  const { getFeatureFlags } = usePlanFeatures();
  const [filters, setFilters] = useState({
    distance: '25',
    newPatientStatus: 'no-preference',
    visitType: 'no-preference',
    gender: 'no-preference'
  });

  // Build dynamic filters based on feature flags and facets
  const allFilters = useMemo(() => {
    const featureFlags = getFeatureFlags();
    const dynamicFilters = buildDynamicFilters(featureFlags, facets);
    
    console.log('🔧 ProviderFilters: Dynamic filters built', {
      featureFlagsAvailable: !!featureFlags,
      facetsAvailable: !!facets,
      totalFilters: dynamicFilters.length,
      mainFilters: dynamicFilters.slice(0, 3).map(f => f.label),
      sidebarFilters: dynamicFilters.slice(3).map(f => f.label)
    });
    
    return dynamicFilters;
  }, [getFeatureFlags, facets]);

  // Use external filters if provided, otherwise use internal state
  const currentFilters = externalFilters || filters;
  // Define handleFilterChange to update local state if no external handler is provided
  function handleFilterChange(filterType, value) {
    if (externalFilterChange) {
      externalFilterChange(filterType, value);
    } else {
      setFilters(prev => ({ ...prev, [filterType]: value }));
    }
  }

  // Which filters are single-select (radio) vs multi-select (checkbox)
  // Main filters (first 4) are always single-select
  const radioFilterKeys = ['distance', 'newPatientStatus', 'gender', 'visitType', 'birthingFriendlyCare', 'agesServed', 'boardCertified'];
  // Sidebar filters are multi-select checkboxes
  const checkboxFilterKeys = ['language', 'specialty', 'acceptsMedicaid', 'pubTrans',
    'groupAffiliation', 'handicapAccess', 'hospitalAffiliation',
    'specializedTraining', 'accreditation', 'afterHoursWeekend', 'providerRace', 'providerEthnicity', 'cshcs', 'specialProviderType'];

  // Pending filter state for sidebar
  const [pendingFilters, setPendingFilters] = useState(currentFilters);
  // Track expanded state for show more/less per filter
  const [expandedFilters, setExpandedFilters] = useState({});
  // Track custom distance input value
  const [customDistance, setCustomDistance] = useState('');
  // Mirrors distancePanel.jsx's identical 250-mile bound on its own custom distance
  // input — this sidebar filter's version had no upper-bound validation at all, so a
  // user could type/apply any number of miles here even though the "Edit distance" panel
  // elsewhere in the app caps custom distance at 250.
  const [distanceError, setDistanceError] = useState('');
  // Maps filter.key -> the DOM node of that filter's .filter-choices options list, so
  // handleShowMore can move focus into the newly-revealed options on expand (see below).
  // Also used as the aria-controls target for the Show more/less toggle button.
  const filterChoicesRefs = useRef({});

  // Handle radio/checkbox change in sidebar
  const handleSidebarChange = (filterKey, value, checked) => {
    if (radioFilterKeys.includes(filterKey)) {
      setPendingFilters(prev => ({ ...prev, [filterKey]: value }));
      // Clear custom distance (and any bounding error on it) if selecting a preset
      // distance instead.
      if (filterKey === 'distance' && value !== 'custom') {
        setCustomDistance('');
        setDistanceError('');
      }
    } else if (checkboxFilterKeys.includes(filterKey)) {
      setPendingFilters(prev => {
        const prevArr = Array.isArray(prev[filterKey]) ? prev[filterKey] : [];
        let nextArr;
        if (checked) {
          nextArr = [...prevArr, value];
        } else {
          nextArr = prevArr.filter(v => v !== value);
        }
        return { ...prev, [filterKey]: nextArr };
      });
    }
  };

  // Handle custom distance input. Mirrors distancePanel.jsx's identical
  // handleCustomDistanceChange — same 250-mile bound, applied here too so this sidebar
  // filter's custom distance can't silently be set (and applied) above that cap the way
  // it previously could, with no validation at all.
  const handleCustomDistanceChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setCustomDistance(value);
      setPendingFilters(prev => ({ ...prev, distance: 'custom' }));

      // Check if value is over 250
      if (value && Number(value) > 250) {
        setDistanceError('Be sure to type a number up to 250 miles.');
      } else {
        setDistanceError('');
      }
    }
  };

  // Show more/less toggle
  // A11y defect fix: this previously only toggled state with no aria-expanded on the
  // button and no focus management, so screen readers (NVDA/JAWS/TalkBack/VoiceOver)
  // announced it as a plain button with no expanded/collapsed state, and focus never
  // moved into the newly-revealed radio options. Now: the button's aria-expanded is
  // driven directly by expandedFilters[filterKey] (see render below), and on expand we
  // move focus to the first newly-revealed option so AT users land directly on the new
  // content instead of having to re-navigate to find it.
  const handleShowMore = (filterKey) => {
    setExpandedFilters(prev => {
      const willExpand = !prev[filterKey];
      if (willExpand) {
        // Wait one frame so the newly-revealed options exist in the DOM before we try
        // to focus one of them.
        requestAnimationFrame(() => {
          const container = filterChoicesRefs.current[filterKey];
          const inputs = container?.querySelectorAll('input');
          // The first 3 options are always visible — index 3 is the first option that
          // just became visible as a result of expanding.
          const firstNewOption = inputs?.[3];
          firstNewOption?.focus();
        });
      }
      return { ...prev, [filterKey]: willExpand };
    });
  };

  // Apply and Reset
  const handleApplyFilters = () => {
    // Prepare filters to apply
    let filtersToApply = { ...pendingFilters };
    
    // If custom distance is selected, use the custom distance value
    if (pendingFilters.distance === 'custom' && customDistance) {
      // Don't allow values over 250 — mirrors distancePanel.jsx's identical guard.
      if (Number(customDistance) > 250) {
        return;
      }
      filtersToApply.distance = customDistance;
    }
    
    // Don't apply if custom is selected but no value entered
    if (pendingFilters.distance === 'custom' && !customDistance) {
      return;
    }
    
    // Use batch update if available, otherwise fall back to individual updates
    if (onBatchFilterChange) {
      onBatchFilterChange(filtersToApply);
    } else {
      Object.entries(filtersToApply).forEach(([key, value]) => handleFilterChange(key, value));
    }
    closeSidebar();
  };
  const handleResetFilters = () => {
    const reset = {};
    allFilters.forEach(f => {
      if (radioFilterKeys.includes(f.key)) {
        // Special case: distance should default to '25', not the first option ('5')
        if (f.key === 'distance') {
          reset[f.key] = '25';
        } else if (f.key === 'newPatientStatus') {
          // New patient status defaults to 'accepting'
          reset[f.key] = 'no-preference';
        } else if (f.key === 'boardCertified') {
          // Board certified doesn't have a 'no-preference' option, so leave it empty/unselected
          reset[f.key] = '';
        } else {
          // For other radio filters, default to 'no-preference' (which is the first option for most)
          reset[f.key] = 'no-preference';
        }
      } else if (checkboxFilterKeys.includes(f.key)) {
        reset[f.key] = [];
      }
    });
    setPendingFilters(reset);
    // Also clear the custom distance input/error — otherwise a previously-entered
    // (and possibly out-of-bounds) custom value would linger visually after Reset.
    setCustomDistance('');
    setDistanceError('');
    
    // Apply the reset filters immediately using batch update
    if (onBatchFilterChange) {
      onBatchFilterChange(reset);
    } else {
      Object.entries(reset).forEach(([key, value]) => handleFilterChange(key, value));
    }
    closeSidebar();
  };


  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Restore focus to whatever triggered the "All filters" sidebar once it closes
  // (the "All Filters" button, or the mobile "View all filters" button).
  useModalFocusReturn(sidebarOpen);

  // Move focus INTO the sidebar once it opens, instead of leaving it on the "All
  // Filters"/"All filters" trigger button behind it — without this, a keyboard/screen
  // reader user's next Tab press continued from that now-hidden trigger button rather
  // than entering the newly-opened sidebar, and the sidebar's content was never
  // announced. Declared after useModalFocusReturn so that hook's "capture the trigger
  // that was focused before opening" effect runs first (see its own doc comment on
  // effect ordering) — this effect's focus-moving happens on the next tick regardless
  // via a separate effect, so ordering between the two doesn't actually matter here,
  // but keeping useModalFocusReturn first preserves the documented convention.
  const sidebarCloseBtnRef = useRef(null);
  
  useEffect(() => {
    if (sidebarOpen) {
      sidebarCloseBtnRef.current?.focus();
    }
  }, [sidebarOpen]);

  // Trap Tab/Shift+Tab within the sidebar while it's open, so keyboard users can't tab
  // out into the (visually hidden-behind-overlay, but still-in-DOM) rest of the page.
  // Same approach as distancePanel.jsx's identical trap.
  const sidebarRef = useRef(null);
  useEffect(() => {
    if (sidebarOpen && sidebarRef.current) {
      const focusableElements = sidebarRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      sidebarRef.current.addEventListener('keydown', handleTabKey);
      return () => {
        sidebarRef.current?.removeEventListener('keydown', handleTabKey);
      };
    }
  }, [sidebarOpen]);

  // Lock body scroll when sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      // Compensate for the scrollbar width removed by body.sidebar-open's
      // `overflow: hidden` (see styles.css) — otherwise the page content jumps
      // sideways by the scrollbar's width the instant the sidebar opens, which is
      // what was reported as the scrollbar looking "covered/overridden by the main
      // page bar". Same fix as useBodyScrollLock.js (used by every other modal/panel
      // in the app) — this sidebar uses its own separate lock mechanism instead of
      // that shared hook, so it needed the identical compensation applied here too.
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        const currentPaddingRight = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
        document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
      }
      document.body.classList.add('sidebar-open');
      // Also lock <html> directly — see useBodyScrollLock.js for the full
      // explanation. In short: App.css sets `html { overflow-x: hidden }`
      // globally, which in some browsers disqualifies body's overflow from
      // propagating to control the actual viewport scroll box, leaving <html>
      // as the real (unlocked) scrolling element in those browsers only.
      document.documentElement.classList.add('sidebar-open-html');
    } else {
      document.body.classList.remove('sidebar-open');
      document.documentElement.classList.remove('sidebar-open-html');
      document.body.style.paddingRight = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('sidebar-open');
      document.documentElement.classList.remove('sidebar-open-html');
      document.body.style.paddingRight = '';
    };
  }, [sidebarOpen]);

  const openSidebar = () => {
    console.log('🔵 openSidebar called - current sidebarOpen:', sidebarOpen);
    // Sync pending filters with current filters when opening sidebar
    setPendingFilters(currentFilters);
    setSidebarOpen(true);
    console.log('🔵 openSidebar - setSidebarOpen(true) called');
  };
  const closeSidebar = () => {
    console.log('🔴 closeSidebar called');
    setSidebarOpen(false);
    // Reset each filter's "Show more" expand/collapse state back to collapsed on close.
    // Without this, expandedFilters (declared once via useState({}) and never otherwise
    // cleared) simply persists across close/reopen cycles — the sidebar is only
    // conditionally RENDERED based on sidebarOpen, not unmounted/remounted, so its state
    // survives closing. That made a section left expanded before closing still show
    // expanded the next time the sidebar was opened, instead of starting fresh collapsed
    // like every other transient sidebar-open state (pendingFilters, customDistance,
    // distanceError) already does.
    setExpandedFilters({});
  };
    const getCurrentFiltersText = () => {
      const activeFilters = [];
      allFilters.slice(0, 4).forEach(filter => {
        // Skip distance filter (now handled by distance panel)
        if (filter.key === 'distance') return;
        
        // Only show filters that have options
        if (filter.options && filter.options.length > 0) {
          const selected = filter.options.find(opt => opt.value === currentFilters[filter.key]);
          if (selected) {
            activeFilters.push(selected.label);
          }
        }
      });
      return activeFilters.length > 0 ? activeFilters.join(', ') : 'No filters available';
    };

  return (
    <div className="provider-filters">
     
      <h2 className="provider-filters-title">Provider filters</h2>
      {/* Mobile Layout */}
      <div className="mobile-filters-layout">
        <p className="mobile-filters-text">
          You can filter your results by more topics. Choose your language. Or find a location that is handicap accessible. You can even find appointments after hours. Choose the &ldquo;All filters&rdquo; button for more filters.
        </p>
        <button
          className="mobile-view-all-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openSidebar();
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openSidebar();
          }}
          type="button"
          aria-label="All filters"
          aria-expanded={sidebarOpen}
          aria-controls="all-filters-sidebar-panel"
        >
          All filters
        </button>
      </div>

      {/* Desktop Layout */}
      <div className="provider-filters-content">
        {/* Filter Description Text */}
        <p className="filters-description">
          You can filter your results by more topics. Choose your language. Or find a location that is handicap accessible. You can even find appointments after hours. Choose the &ldquo;All filters&rdquo; button for more filters.
        </p>
        
        <div className="filters-row">
          {allFilters.slice(0, 4).map(filter => (
            // Skip distance filter in main section (now handled by distance panel)
            // Only render filters that have options
            filter.key !== 'distance' && filter.options && filter.options.length > 0 && (
              <div className="filter-group" key={filter.key}>
                <label htmlFor={`${filter.key}-filter`} id={`${filter.key}-filter-label`} className="filter-label">
                  {filter.label}
                  {filter.hasInfoIcon && (
                    <InfoTooltip
                      heading={filter.infoHeading}
                      body={filter.infoBody}
                    />
                  )}
                </label>
                <Dropdown
                  id={`${filter.key}-filter`}
                  className="filter-dropdown"
                  value={currentFilters[filter.key]}
                  onChange={e => handleFilterChange(filter.key, e.target.value)}
                  options={filter.options}
                  aria-label={`${filter.label} filter`}
                  aria-labelledby={`${filter.key}-filter-label`}
                />
              </div>
            )
          ))}
          {/* All filters button - moved inside filters-row */}
          {/* A11y fix: this disclosure button opens/closes the "All filters" sidebar
              (see sidebarOpen below), but had no aria-expanded — screen readers
              announced it as a plain button with no indication of whether the panel
              it controls is currently open or closed. aria-expanded (tied directly to
              sidebarOpen) plus aria-controls (pointing at the sidebar's own id) gives
              AT users that state, matching the same pattern already used for the
              "Show more/less" filter-option toggles below. */}
          <button 
            className="all-filters-button"
            onClick={openSidebar}
            type="button"
            aria-expanded={sidebarOpen}
            aria-controls="all-filters-sidebar-panel"
          >
            All filters
          </button>
        </div>
        {/* Sidebar for all filters - rendered in a portal to avoid parent CSS transform issues */}
        {sidebarOpen && createPortal(
          <div className="provider-filters-sidebar-overlay" onClick={closeSidebar}>
            <div 
              className="provider-filters-sidebar" 
              id="all-filters-sidebar-panel"
              ref={sidebarRef}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => { if (e.key === 'Escape') closeSidebar(); }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="all-filters-title"
            >
              <button className="sidebar-close-btn" ref={sidebarCloseBtnRef} onClick={closeSidebar} aria-label="Close filters">
                <img src={closeIcon} alt="" />
              </button>
              <h3 id="all-filters-title" className="sidebar-title">All filters</h3>
              <div className="sidebar-filters-list">
                {allFilters
                  .filter(filter => filter.options && filter.options.length > 0) // Only show filters with options
                  .map((filter, idx) => (
                  <div className="filter-group" key={filter.key} style={{paddingLeft:24, paddingRight:24, marginTop: idx === 0 ? 24 : 0, marginBottom: 0}}>
                    <div 
                      className="filter-label" 
                      id={`filter-label-${filter.key}`}
                      style={{
                        cursor: expandedFilters[filter.key] && filter.options.length > 3 ? 'pointer' : 'default'
                      }}
                      onClick={() => {
                        // Only make clickable if expanded and has more than 3 options
                        if (expandedFilters[filter.key] && filter.options.length > 3) {
                          handleShowMore(filter.key);
                        }
                      }}
                    >
                      {filter.label}
                      {filter.hasInfoIcon && (
                        <InfoTooltip
                          heading={filter.infoHeading}
                          body={filter.infoBody}
                        />
                      )}
                    </div>
                    <div
                      className="filter-choices"
                      id={`filter-choices-${filter.key}`}
                      ref={(el) => { filterChoicesRefs.current[filter.key] = el; }}
                      // A11y fix: screen readers (NVDA/JAWS/TalkBack/VoiceOver) were
                      // announcing each radio/checkbox input's own <label> text but never
                      // the overall group name (e.g. "Distance") on entering the group or
                      // moving to its first control, because these inputs had no
                      // programmatic group container at all — just a name="" attribute,
                      // which groups radios for form-submission purposes only, not for
                      // accessible-name purposes. role="radiogroup"/"group" +
                      // aria-labelledby pointing at the existing .filter-label heading
                      // (id="filter-label-<key>" above) gives AT a real group to announce
                      // the label for.
                      role={radioFilterKeys.includes(filter.key) ? 'radiogroup' : 'group'}
                      aria-labelledby={`filter-label-${filter.key}`}
                    >
                      {(expandedFilters[filter.key] ? filter.options : filter.options.slice(0,3)).map(option => (
                        radioFilterKeys.includes(filter.key) ? (
                          option.isCustom ? (
                            // Special handling for custom distance input
                            <label key={option.value} className="filter-choice-label filter-custom-option">
                              <div className="custom-distance-row">
                                <input
                                  type="radio"
                                  name={filter.key}
                                  value={option.value}
                                  checked={pendingFilters[filter.key] === option.value}
                                  onChange={() => handleSidebarChange(filter.key, option.value, true)}
                                  className="custom-radio"
                                />
                                <span className="custom-distance-wrapper">
                                  Within
                                  <input
                                    type="text"
                                    className={`custom-distance-input${distanceError ? ' error' : ''}`}
                                    value={customDistance}
                                    onChange={handleCustomDistanceChange}
                                    onFocus={() => handleSidebarChange(filter.key, 'custom', true)}
                                    placeholder=""
                                    aria-label="Custom distance in miles"
                                    aria-invalid={distanceError ? 'true' : 'false'}
                                    aria-describedby={distanceError ? 'sidebar-distance-error-msg' : undefined}
                                    maxLength="4"
                                  />
                                  miles
                                </span>
                              </div>
                              {/* Bounding error message — mirrors distancePanel.jsx's identical
                                  "Edit distance" panel error, so the two custom-distance inputs
                                  in this app behave and communicate the same 250-mile cap. */}
                              {distanceError && (
                                <div id="sidebar-distance-error-msg" className="custom-distance-error-message" role="alert">
                                  <img src={hazardIcon} alt="" className="error-icon" />
                                  <span>{distanceError}</span>
                                </div>
                              )}
                            </label>
                          ) : (
                            <label key={option.value} className="filter-choice-label">
                              <input
                                type="radio"
                                name={filter.key}
                                value={option.value}
                                checked={pendingFilters[filter.key] === option.value}
                                onChange={() => handleSidebarChange(filter.key, option.value, true)}
                                className="custom-radio"
                              />
                              <span style={{display:'inline-flex',alignItems:'center'}}>{option.label}{option.hasInfoIcon && <InfoTooltip heading={option.infoHeading} body={option.infoBody} />}</span>
                            </label>
                          )
                        ) : (
                          <label key={option.value} className="filter-choice-label">
                            <input
                              type="checkbox"
                              name={filter.key}
                              value={option.value}
                              checked={Array.isArray(pendingFilters[filter.key]) && pendingFilters[filter.key].includes(option.value)}
                              onChange={e => handleSidebarChange(filter.key, option.value, e.target.checked)}
                              className="custom-checkbox"
                            />
                            <span style={{display:'inline-flex',alignItems:'center'}}>{option.label}{option.hasInfoIcon && <InfoTooltip heading={option.infoHeading} body={option.infoBody} />}</span>
                          </label>
                        )
                      ))}
                      {/* Naive pluralization (lowercase label + "s") breaks for any filter
                          whose label is ALREADY plural (e.g. "Group affiliations" ->
                          "group affiliationss", "Hospital affiliations" ->
                          "hospital affiliationss" — a double-s typo) or otherwise
                          grammatically awkward ("Specialized training" -> "specialized
                          trainings", "Provider race" -> "provider races", "Handicap
                          accessibility" -> "handicap accessibilitys"). FILTER_NAME_PLURAL_OVERRIDES
                          covers every sidebar filter that can exceed 3 options (the only
                          ones this button ever renders for) and needs non-naive handling;
                          anything not listed still falls back to the naive lowercase + "s"
                          (safe for singular labels like "Language" -> "languages"). Shared
                          by both the "Show more"/"Show less" buttons below so they always
                          name the same filter consistently. */}
                      {(() => {
                        const FILTER_NAME_PLURAL_OVERRIDES = {
                          specialty: 'specialties',
                          language: 'languages spoken',
                          groupAffiliation: 'group affiliations',
                          hospitalAffiliation: 'hospital affiliations',
                          handicapAccess: 'handicap accessibility needs',
                          specializedTraining: 'special skills',
                          providerRace: 'provider races',
                          providerEthnicity: 'races/ethnicities',
                          cshcs: 'CSHCS options',
                          specialProviderType: 'special provider type options'
                        };
                        const filterNamePlural = FILTER_NAME_PLURAL_OVERRIDES[filter.key] || `${filter.label.toLowerCase()}s`;
                        const isExpanded = !!expandedFilters[filter.key];
                        return (
                          filter.options.length > 3 && (
                            // A11y fix: this used to be TWO separate conditionally-rendered
                            // <button> elements (one for collapsed, one for expanded), so
                            // toggling swapped the DOM node entirely — losing focus back to
                            // <body> instead of keeping it on the toggle, and giving screen
                            // readers no persistent element to track state on. Now it's a
                            // single button whose label/icon/aria-expanded all just react to
                            // isExpanded, and aria-controls ties it to the options list
                            // (#filter-choices-<key>) so AT announces what it expands/
                            // collapses. Focus-into-revealed-content on expand is handled in
                            // handleShowMore via filterChoicesRefs.
                            <button
                              className="show-more-btn"
                              style={{color:'#7D3F98',background:'none',border:'none',padding:0,marginTop:10,cursor:'pointer',display:'flex',alignItems:'center',gap:10}}
                              onClick={() => handleShowMore(filter.key)}
                              aria-expanded={isExpanded}
                              aria-controls={`filter-choices-${filter.key}`}
                            >
                              {isExpanded ? `Show fewer ${filterNamePlural}` : `Show all ${filterNamePlural}`}
                              <img src={arrowDown} alt="" style={{width:17,height:11,transform: isExpanded ? 'rotate(180deg)' : 'none'}} />
                            </button>
                          )
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{padding:'24px',borderTop:'1px solid #eee',marginTop:'auto',display:'flex',flexDirection:'column',gap:16}}>
                <button className="apply-filters-btn" style={{display:'inline-flex',padding:'12px 24px',justifyContent:'center',alignItems:'center',gap:16,borderRadius:8,background:'#7D3F98',color:'#fff',fontFamily:'CVS Health Sans',fontSize:16,fontWeight:500,border:'none',cursor:'pointer'}} onClick={handleApplyFilters}>
                  Apply new filters
                </button>
                <button className="reset-filters-link" style={{display:'inline-flex',width:'100%',justifyContent:'center',alignItems:'center',gap:10,background:'none',border:'none',color:'#7D3F98',fontFamily:'CVS Health Sans',fontSize:15,fontWeight:400,cursor:'pointer',textDecoration:'none'}} onClick={handleResetFilters}>
                  Reset all filters
                </button>
              </div>
            </div>
          </div>
        , document.body)}
      </div>
    </div>
  );
};

export default ProviderFilters;